import React, { useState } from 'react';
import {
  MapPin, Clock, Camera, Package, MessageSquare, PenTool,
  CheckCircle2, Play, ArrowLeft, ChevronDown, ChevronUp, AlertTriangle,
} from 'lucide-react';
import { TechnicianJob, JobStatus, PhotoStage } from '../../types/technicianJob';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { PhotoCapture } from './PhotoCapture';
import { MaterialLogger } from './MaterialLogger';
import { ProgressTimeline } from './ProgressTimeline';
import { DigitalSignature } from './DigitalSignature';

interface TechnicianJobCardProps {
  job: TechnicianJob;
  onAccept: (jobId: string) => void;
  onStart: (jobId: string) => void;
  onAddPhoto: (jobId: string, photo: { stage: PhotoStage; data_url: string; captured_at: string; notes?: string }) => void;
  onRemovePhoto: (jobId: string, photoId: string) => void;
  onAddMaterial: (jobId: string, material: { part_name: string; quantity: number; unit: string; unit_cost: number }) => void;
  onRemoveMaterial: (jobId: string, materialId: string) => void;
  onComplete: (jobId: string, notes: string, signature?: string) => void;
  onBack: () => void;
}

const STATUS_CONFIG: Record<JobStatus, { label: string; variant: 'info' | 'warning' | 'success' | 'danger' | 'neutral' }> = {
  assigned: { label: 'Assigned', variant: 'info' },
  accepted: { label: 'Accepted', variant: 'success' },
  in_progress: { label: 'In Progress', variant: 'warning' },
  pending_inspection: { label: 'Pending Inspection', variant: 'info' },
  completed: { label: 'Completed', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'danger' },
};

const PRIORITY_MAP: Record<string, { label: string; variant: 'info' | 'warning' | 'danger' | 'neutral' }> = {
  low: { label: 'Low', variant: 'info' },
  medium: { label: 'Medium', variant: 'warning' },
  high: { label: 'High', variant: 'danger' },
  critical: { label: 'Critical', variant: 'danger' },
};

function isOverdue(slaDeadline: string): boolean {
  return new Date(slaDeadline) < new Date();
}

export const TechnicianJobCard: React.FC<TechnicianJobCardProps> = ({
  job, onAccept, onStart, onAddPhoto, onRemovePhoto, onAddMaterial, onRemoveMaterial, onComplete, onBack,
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>('details');
  const [completionNotes, setCompletionNotes] = useState('');
  const [savedSignature, setSavedSignature] = useState<string | undefined>();

  const toggle = (section: string) => setExpandedSection(expended => expended === section ? null : section);

  const config = STATUS_CONFIG[job.status];
  const priorityConf = PRIORITY_MAP[job.priority] || PRIORITY_MAP.medium;
  const overdue = isOverdue(job.sla_deadline);

  const canAccept = job.status === 'assigned';
  const canStart = job.status === 'accepted';
  const canWork = job.status === 'in_progress';
  const canComplete = job.status === 'in_progress';

  const totalMaterialCost = job.materials.reduce((sum, m) => sum + m.total_cost, 0);

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-xs transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Jobs
      </button>

      <Card
        title={job.title}
        subtitle={job.work_order_id}
        action={
          <div className="flex items-center gap-2">
            {overdue && <Badge label="OVERDUE" variant="danger" />}
            <Badge label={config.label} variant={config.variant} />
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-500 font-medium">Location</p>
                <p className="text-xs text-slate-300">{job.location}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-500 font-medium">SLA Deadline</p>
                <p className={`text-xs font-semibold ${overdue ? 'text-rose-400' : 'text-slate-300'}`}>
                  {new Date(job.sla_deadline).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-500 font-medium">Priority / Category</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Badge label={priorityConf.label} variant={priorityConf.variant} />
                  <span className="text-xs text-slate-400">{job.category}</span>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-500 font-medium">Complaint ID</p>
                <p className="text-xs text-sky-400 font-mono">{job.complaint_id}</p>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400 leading-relaxed">{job.description}</p>

          {(canAccept || canStart || canComplete) && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800">
              {canAccept && (
                <Button size="sm" variant="primary" icon={<CheckCircle2 className="w-3.5 h-3.5" />} onClick={() => onAccept(job.job_id)}>
                  Accept Job
                </Button>
              )}
              {canStart && (
                <Button size="sm" variant="primary" icon={<Play className="w-3.5 h-3.5" />} onClick={() => onStart(job.job_id)}>
                  Start Work
                </Button>
              )}
              {canComplete && (
                <Button size="sm" variant="primary" icon={<CheckCircle2 className="w-3.5 h-3.5" />} onClick={() => onComplete(job.job_id, completionNotes, savedSignature)}>
                  Mark Complete
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Progress Timeline */}
      <div className="bg-slate-800 border border-slate-700/60 rounded-xl overflow-hidden">
        <button
          onClick={() => toggle('timeline')}
          className="w-full flex items-center justify-between px-5 py-3 text-left"
        >
          <span className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" /> Progress Timeline
            <span className="text-[10px] text-slate-500 font-normal">({job.progress_updates.length})</span>
          </span>
          {expandedSection === 'timeline' ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </button>
        {expandedSection === 'timeline' && (
          <div className="px-5 pb-4">
            <ProgressTimeline updates={job.progress_updates} />
          </div>
        )}
      </div>

      {/* Photos */}
      <div className="bg-slate-800 border border-slate-700/60 rounded-xl overflow-hidden">
        <button
          onClick={() => toggle('photos')}
          className="w-full flex items-center justify-between px-5 py-3 text-left"
        >
          <span className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <Camera className="w-4 h-4 text-slate-400" /> Work Photos
            <span className="text-[10px] text-slate-500 font-normal">({job.photos.length})</span>
          </span>
          {expandedSection === 'photos' ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </button>
        {expandedSection === 'photos' && (
          <div className="px-5 pb-4">
            <PhotoCapture
              photos={job.photos}
              onCapture={(photo) => onAddPhoto(job.job_id, photo)}
              onRemove={(photoId) => onRemovePhoto(job.job_id, photoId)}
              readonly={!canWork}
            />
          </div>
        )}
      </div>

      {/* Materials */}
      <div className="bg-slate-800 border border-slate-700/60 rounded-xl overflow-hidden">
        <button
          onClick={() => toggle('materials')}
          className="w-full flex items-center justify-between px-5 py-3 text-left"
        >
          <span className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <Package className="w-4 h-4 text-slate-400" /> Materials & Parts
            <span className="text-[10px] text-slate-500 font-normal">({job.materials.length})</span>
            {totalMaterialCost > 0 && <span className="text-[10px] text-sky-400 font-semibold">{totalMaterialCost.toFixed(2)}</span>}
          </span>
          {expandedSection === 'materials' ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </button>
        {expandedSection === 'materials' && (
          <div className="px-5 pb-4">
            <MaterialLogger
              materials={job.materials}
              onAdd={(mat) => onAddMaterial(job.job_id, mat)}
              onRemove={(matId) => onRemoveMaterial(job.job_id, matId)}
              readonly={!canWork}
            />
          </div>
        )}
      </div>

      {/* Completion Notes & Signature */}
      {canComplete && (
        <div className="bg-slate-800 border border-slate-700/60 rounded-xl overflow-hidden">
          <button
            onClick={() => toggle('completion')}
            className="w-full flex items-center justify-between px-5 py-3 text-left"
          >
            <span className="text-sm font-semibold text-slate-100 flex items-center gap-2">
              <PenTool className="w-4 h-4 text-slate-400" /> Completion Details
            </span>
            {expandedSection === 'completion' ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
          </button>
          {expandedSection === 'completion' && (
            <div className="px-5 pb-4 space-y-4">
              <textarea
                value={completionNotes}
                onChange={e => setCompletionNotes(e.target.value)}
                placeholder="Describe the work completed, findings, recommendations..."
                rows={3}
                className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 placeholder:text-slate-600 focus:ring-2 focus:ring-sky-500 resize-none"
              />
              <DigitalSignature onSave={(sig) => setSavedSignature(sig)} />
              {savedSignature && (
                <div className="flex items-center gap-2 text-xs text-emerald-400">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Signature saved
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
